import React, { useEffect, useState, useCallback, useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { getUrl } from "v2/helpers/url";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import EnquiryList from "./list";
import SendDocumentModal from "v2/apps/shared/components/send-document-modal";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useContext } from "hooks/context";
import { CONSTANTS } from "clink-components";

const { clinkGreen, clinkRed, white } = CONSTANTS.colors.general;

const getWorkPackages = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {};
  }

  if (
    data.tenders &&
    typeof data.tenders === "object" &&
    !Array.isArray(data.tenders)
  ) {
    return data.tenders;
  }

  return data;
};

const EnquiryIssued = ({ project, contextType = "clink" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const context = useContext(contextType);
  const { actions } = context;
  const { status, projectEnquiries } = useSelector((state) => state.project);

  const [searchQuery, setSearchQuery] = useState("");
  const [assets, setAssets] = useState([]);

  const loading = status === "loading";
  const error = status === "error";
  const workPackages = useMemo(
    () => getWorkPackages(projectEnquiries),
    [projectEnquiries],
  );
  const hasWorkPackages = Object.keys(workPackages).length > 0;

  useEffect(() => {
    if (project?.id) {
      dispatch(actions.fetchProjectEnquiries({ pid: project.id }));
    }
  }, [dispatch, actions, project?.id]);

  useEffect(() => {
    let isActive = true;

    const fetchAssets = async () => {
      try {
        const result = await dispatch(actions.fetchTemplates({ type: "tenders" }));
        if (isActive && result?.payload) {
          setAssets(result.payload);
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
      }
    };

    fetchAssets();

    return () => {
      isActive = false;
    };
  }, [dispatch, actions]);

  const createTenderTemplate = useCallback(
    async (pid, tid, did, loi = false) => {
      try {
        const result = loi
          ? await dispatch(actions.createLoiTemplate({ pid, tid }))
          : await dispatch(actions.createTenderTemplate({ pid, tid, did }));
        const docId = result?.payload?.docId;

        if (docId) {
          navigate(
            loi
              ? `/document-creator/template/${docId}/loi/${tid}`
              : `/document-creator/template/${docId}/tender/${tid}`,
          );
          dispatch(actions.fetchProjectEnquiries({ pid: project.id }));
        }

        return docId;
      } catch (err) {
        console.error("Error creating template:", err);
        return null;
      }
    },
    [dispatch, actions, navigate, project?.id],
  );

  const handleTemplateDelete = useCallback(() => {
    if (project?.id) {
      dispatch(actions.fetchProjectEnquiries({ pid: project.id }));
    }
  }, [dispatch, actions, project?.id]);

  const handleEnquirySent = useCallback(() => {
    if (project?.id) {
      dispatch(actions.fetchProjectEnquiries({ pid: project.id }));
    }
  }, [dispatch, actions, project?.id]);

  const ready = !loading && !error;

  return (
    <Box
      id="clink-container"
      sx={{
        flex: 1,
      }}
    >
      {ready && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: white,
            p: "1.2rem 1.4rem",
            mb: 0,
            borderTopRightRadius: "0.5rem",
            borderTopLeftRadius: "0.5rem",
            borderBottom: "none",
            alignItems: "center",
            gap: 2,
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder={t("search-by-company")}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 250 }}
          />

          {hasWorkPackages && (
            <Button
              component="a"
              href={getUrl("CLINK_APP_HOST", `/reports/tender/${project.id}`)}
              download
              sx={{
                cursor: "pointer",
                backgroundColor: clinkGreen,
                color: white,
                display: "flex",
                alignItems: "center",
                borderRadius: "4px",
                fontSize: "16px",
                padding: "4px 10px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: clinkRed,
                  color: white,
                },
              }}
            >
              {t("download-tender-report")}
            </Button>
          )}
        </Box>
      )}

      {loading && <CircularProgress className="loading-status" />}
      {error && <Alert severity="error">{t("error-fetching-quotes")}</Alert>}

      {ready && (
        <Box
          sx={{
            width: "100%",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            backgroundColor: white,
          }}
        >
          <EnquiryList
            items={workPackages}
            projectData={project}
            assets={assets}
            createTemplate={createTenderTemplate}
            onTemplateDelete={handleTemplateDelete}
            searchQuery={searchQuery}
          />
        </Box>
      )}
      <SendDocumentModal callback={handleEnquirySent} isUnifiedPage />
    </Box>
  );
};

export default EnquiryIssued;
